import { useState } from 'react';
import { ChevronDown, Shield, User as UserIcon } from 'lucide-react';
import type { PortalRole } from '@solara/sample-data';
import { useTranslation } from '@solara/i18n';
import { Button, cn } from '@solara/ui-shared';
import { useAuth } from './AuthContext';

const ROLES: PortalRole[] = ['CustomerAdmin', 'Accountant', 'Approver', 'Viewer'];

export function RoleSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation('common');
  const { currentUser, setRole } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('relative', className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((s) => !s)}
        className="gap-2 text-xs"
        title={t('demo.roleSwitcherTitle')}
      >
        <Shield className="h-4 w-4 text-brand-600" />
        <span className="hidden sm:inline">{t(`roles.${currentUser.role}`)}</span>
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      {open ? (
        <div
          className="absolute end-0 mt-2 w-72 z-30 rounded-xl border border-border bg-surface shadow-lg p-2"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="px-2 py-1.5 text-[11px] uppercase tracking-wider text-fg-subtle">
            {t('demo.switchRole')}
          </div>
          {ROLES.map((r) => {
            const active = r === currentUser.role;
            return (
              <button
                key={r}
                type="button"
                onClick={() => {
                  setRole(r);
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-start rounded-md px-3 py-2 text-sm flex items-center gap-2',
                  'transition-colors',
                  active
                    ? 'bg-brand-50 text-brand-800 font-medium'
                    : 'text-fg-muted hover:bg-surface-muted hover:text-fg',
                )}
              >
                <UserIcon className="h-4 w-4" />
                <span>{t(`roles.${r}`)}</span>
              </button>
            );
          })}
          <div
            className="mt-1 pt-2 border-t border-border px-3 py-1.5 text-[11px] text-fg-subtle"
            // i18next renders <bold>...</bold> as plain text; we post-process so
            // the email stays visually bold across locales without HTML escaping.
            dangerouslySetInnerHTML={{
              __html: t('demo.loggedInAs', { email: currentUser.email })
                .replace('<bold>', '<span class="font-medium text-fg">')
                .replace('</bold>', '</span>'),
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
